import { Location } from "@angular/common";
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { LoggingService } from "@merri/common";
import { BehaviorSubject, Observable, OperatorFunction, throwError } from "rxjs";
import { catchError, concatMap, filter, finalize, take } from "rxjs/operators";
import {
  AppConfigService,
  AuthService,
  DEFAULT_GUEST_REDIRECT_URL,
  DESING_GUEST_EXPIRED_ROUTE_PATH_FULL,
  EVENT_SUMMARY_GUEST_EXPIRED_ROUTE_PATH_FULL,
  SessionService,
  SHOWROOM_GUEST_EXPIRED_ROUTE_PATH_FULL,
  USER_TYPE_DESIGN_GUEST,
  USER_TYPE_EVENT_SUMMARY_GUEST,
  USER_TYPE_SHOWROOM_GUEST,
} from "../services";

@Injectable({
  providedIn: "root",
})
export class AuthInterceptor implements HttpInterceptor {
  private refreshingToken: boolean;
  private refreshingTokenSubject$: BehaviorSubject<boolean>;

  constructor(
    private authService: AuthService,
    private sessionService: SessionService,
    private appConfigService: AppConfigService,
    private ngZone: NgZone,
    private location: Location,
    private matDialog: MatDialog,
  ) {
    LoggingService.log("AuthInterceptor ctor");

    this.refreshingTokenSubject$ = new BehaviorSubject<boolean>(false);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    LoggingService.log("AuthInterceptor intercept");

    // need this to work with AD auth (dev only?)
    request = request.clone({ withCredentials: true });

    // if we do not need authorization (e.g. not hitting our API), then simply return
    if (!this.shouldAuthorize(request.url)) {
      LoggingService.log("AuthInterceptor skipping authorization");
      return next.handle(request);
    }

    // otherwise, add authorization and handle request
    request = this.addAuthorization(request);

    return next.handle(request).pipe(this.handleErorr(request, next));
  }

  private handleErorr(request: HttpRequest<any>, next: HttpHandler, retry?: boolean): OperatorFunction<HttpEvent<any>, HttpEvent<any>> {
    return catchError((error: HttpErrorResponse) => {
      if (error) {
        if (this.isGuestUserRequest(request)) {
          // force logout guest if 401 or 403
          if (error.status === 401 || error.status === 403) {
            this.forceLogout(request, true);
          }
        } else if (error.status === 401) {
          // if we caught a 401 (unauthorized) we can attempt to refresh our access token
          const tokenExpired = error.headers.get("token-expired") === "true";

          LoggingService.log(`AuthInterceptor 401 - TokenExpired? ${tokenExpired}`);

          // if API told us the token has expired, attempt to refresh it
          // if we success, retry request
          // if we fail, force logout

          if (tokenExpired) {
            return this.refreshToken$(request, next, error, retry);
          } else {
            this.forceLogout(request);
          }
        }
      }

      return throwError(error);
    });
  }

  private refreshToken$(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
    secondAttempt?: boolean,
  ): Observable<HttpEvent<any>> {
    LoggingService.log(`AuthInterceptor - refreshToken$ - ${this.refreshingToken} ${secondAttempt}`);

    if (!this.refreshingToken || secondAttempt) {
      this.refreshingToken = true;
      this.refreshingTokenSubject$.next(false);

      return this.authService.refreshToken$().pipe(
        concatMap(success => {
          if (success) {
            LoggingService.log(`AuthInterceptor - refreshToken$ - refreshing - success`);

            const obs = secondAttempt
              ? next.handle(this.addAuthorization(request)) // only let it retry on the first try
              : next.handle(this.addAuthorization(request)).pipe(this.handleErorr(request, next, true));

            return obs.pipe(
              finalize(() => {
                this.refreshingTokenSubject$.next(true);
              }),
            );
          } else {
            LoggingService.log(`AuthInterceptor - refreshToken$ - refreshing - fail`);

            this.forceLogout(request);

            return throwError(error);
          }
        }),
        finalize(() => {
          LoggingService.log("AuthInterceptor - refreshToken$ - refreshing - finalize");

          this.refreshingToken = false;
        }),
      );
    } else {
      return this.refreshingTokenSubject$.pipe(
        filter(success => {
          LoggingService.log(`AuthInterceptor - refreshToken$ - already refreshing - filter ${success}`);

          return !!success;
        }),
        take(1),
        concatMap(() => {
          LoggingService.log("AuthInterceptor - refreshToken$ - already refreshing - done");

          return next.handle(this.addAuthorization(request));
        }),
      );
    }
  }

  private getGuestExpiredPath(url: string): string {
    const config = this.appConfigService.getProtectedConfig();

    if (url === config?.designGuestInfoGetApiUrl) {
      return DESING_GUEST_EXPIRED_ROUTE_PATH_FULL;
    } else if (url === config?.showroomGuestInfoGetApiUrl) {
      return SHOWROOM_GUEST_EXPIRED_ROUTE_PATH_FULL;
    } else if (url === config?.eventSummaryGuestInfoGetApiUrl) {
      return EVENT_SUMMARY_GUEST_EXPIRED_ROUTE_PATH_FULL;
    }

    const userType = this.sessionService.getUserType();
    if (userType === USER_TYPE_DESIGN_GUEST) {
      return DESING_GUEST_EXPIRED_ROUTE_PATH_FULL;
    } else if (userType === USER_TYPE_SHOWROOM_GUEST) {
      return SHOWROOM_GUEST_EXPIRED_ROUTE_PATH_FULL;
    } else if (userType === USER_TYPE_EVENT_SUMMARY_GUEST) {
      return EVENT_SUMMARY_GUEST_EXPIRED_ROUTE_PATH_FULL;
    }

    return DEFAULT_GUEST_REDIRECT_URL;
  }

  private shouldAuthorize(url: string): boolean {
    const config = this.appConfigService.getConfig();

    return config && url && config.shouldAuthorize(url);
  }

  private isGuestUserRequest(request: HttpRequest<any>): boolean {
    return this.isGuestLoginEndpoint(request.url) || this.sessionService.isGuestToken(request.headers.get("Authorization"));
  }

  private isGuestLoginEndpoint(url: string) {
    const config = this.appConfigService.getProtectedConfig();

    return config && url && config.isGuestLoginEndpoint(url);
  }

  private forceLogout(request: HttpRequest<any>, guestLogout?: boolean): void {
    if (!guestLogout && this.sessionService.isGuestUser()) {
      return;
    }

    //ngZone is needed bc this method seems to be called from outside angular zone
    this.ngZone.run(() => {
      let location = guestLogout ? this.getGuestExpiredPath(request.url) : this.location.path();

      // if were forcing a logout during saml auth, we should give up
      if (location?.startsWith("/saml")) {
        location = `/login?e=Session expired, try again`;
      }

      LoggingService.log(`Forcing logout - redirectUrl: ${location}`);

      // close any dialogs that are open
      this.matDialog.closeAll();

      this.sessionService.logout(location, !guestLogout);
    });
  }

  private addAuthorization(request: HttpRequest<any>): HttpRequest<any> {
    // what if user auth exists in guest mode? should we use that for some things like feedback?

    // Do not attempt to authorize guest pre-login API calls (handled by caller)
    if (this.isGuestLoginEndpoint(request.url)) return request;

    // We need session auth bc guest user auth is temp and therefore not stored in AuthService
    let currentAuth = this.sessionService.getSessionAuth();
    if (currentAuth && currentAuth.userAuthToken) {
      const headers = {};
      headers[currentAuth.authHeader] = currentAuth.userAuthToken;
      request = request.clone({
        setHeaders: headers,
      });
    }

    return request;
  }
}
